import React, { useState } from "react"
import { motion } from "framer-motion"
import {Mail, MapPin, Globe, Send} from 'lucide-react'

const ContactInfo = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  })
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Form submission logic would go here
    setSubmitted(true)
    setTimeout(() => setSubmitted(false), 3000)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <div className="min-h-screen bg-zinc-900 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Contact Information
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Have questions about shed moving equipment? We're here to provide information and answer your inquiries.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Information Cards */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6"
          >
            <div className="bg-zinc-800 p-8 rounded-lg border border-zinc-700">
              <div className="flex items-start gap-4">
                <div className="bg-orange-500/10 p-3 rounded-lg">
                  <Mail className="text-orange-500" size={28} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Email</h3>
                  <p className="text-gray-400 mb-2">
                    For general inquiries and information requests:
                  </p>
                  <a href="mailto:info@muleshed.com" className="text-orange-500 hover:text-orange-400 font-semibold">
                    info@muleshed.com
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-zinc-800 p-8 rounded-lg border border-zinc-700">
              <div className="flex items-start gap-4">
                <div className="bg-orange-500/10 p-3 rounded-lg">
                  <Globe className="text-orange-500" size={28} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Website</h3>
                  <p className="text-gray-400 mb-2">
                    Visit our main website for more information:
                  </p>
                  <a href="https://muleshed.com" className="text-orange-500 hover:text-orange-400 font-semibold">
                    www.muleshed.com
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-zinc-800 p-8 rounded-lg border border-zinc-700">
              <div className="flex items-start gap-4">
                <div className="bg-orange-500/10 p-3 rounded-lg">
                  <MapPin className="text-orange-500" size={28} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Service Area</h3>
                  <p className="text-gray-400">
                    Providing educational resources about shed moving equipment to professionals and businesses nationwide.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-orange-500/10 p-6 rounded-lg border border-orange-500/30">
              <h3 className="text-lg font-bold text-white mb-2">Business Hours</h3>
              <p className="text-gray-300">
                Monday - Friday: 9:00 AM - 5:00 PM EST<br />
                Saturday - Sunday: Closed
              </p>
              <p className="text-sm text-gray-400 mt-3">
                Email inquiries are typically responded to within 1-2 business days.
              </p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="bg-zinc-800 p-8 rounded-lg border border-zinc-700">
              <h2 className="text-2xl font-bold text-white mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-300 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-semibold text-gray-300 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors"
                    placeholder="What is this regarding?"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-orange-500 transition-colors resize-none"
                    placeholder="Tell us how we can help..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl shadow-orange-500/30 flex items-center justify-center gap-2"
                >
                  <Send size={20} />
                  Send Message
                </button>

                {submitted && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-500/10 border border-green-500 text-green-400 px-4 py-3 rounded-lg text-center"
                  >
                    Thank you! Your message has been sent successfully.
                  </motion.div>
                )}
              </form>
            </div>
          </motion.div>
        </div>

        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 bg-zinc-800 p-6 rounded-lg border border-zinc-700"
        >
          <h3 className="text-lg font-bold text-white mb-3">Important Notice</h3>
          <p className="text-gray-400">
            MuleShed.com is an educational and informational resource. We are not affiliated with, sponsored by, or endorsed by Cardinal Manufacturing LLC. "The Mule" is a registered trademark of its respective owner. All information provided is for educational purposes only.
          </p>
        </motion.div>
      </div>
    </div>
  )
}

export default ContactInfo